/**
 * Customer-facing Account Statement activity labels and badges.
 * Pure helpers — no finance recalculation.
 */
import type {
  ClarityOneStatementActivityBadge,
  ClarityOneStatementTxnKind,
} from "./clarityOneStatementOfAccount";

const INTERNAL_TERMS =
  /\b(group\s*billing|allocation|allocated to|parent invoice|revision|commercial head|finance posting|instalment stage)\b/gi;

export function stripStatementInternalTerminology(text: string): string {
  return String(text ?? "")
    .replace(INTERNAL_TERMS, " ")
    .replace(/\s{2,}/g, " ")
    .replace(/\s·\s·/g, " · ")
    .replace(/^[\s·]+|[\s·]+$/g, "")
    .trim();
}

/**
 * Map raw invoice/instalment text to a short customer-friendly activity title.
 */
export function resolveStatementActivityLabel(input: {
  kind: ClarityOneStatementTxnKind;
  sourceText?: string | null;
  voNumber?: string | null;
}): string {
  const kind = input.kind;
  if (kind === "acceptance") {
    if (input.voNumber) return "Variation Order Approved";
    return "Proposal Approved";
  }
  if (kind === "payment") return "Payment Received";
  if (kind === "credit_note") return "Credit Note";
  if (kind === "refund") return "Refund";

  const raw = stripStatementInternalTerminology(String(input.sourceText ?? ""));
  const lower = raw.toLowerCase();

  if (/vo[-\s_]?\d+|variation\s*order/.test(lower) && /deposit|confirm/.test(lower)) {
    const pct = raw.match(/(\d{1,3})\s*%/);
    return pct ? `${pct[1]}% Variation Order Deposit` : "Variation Order Deposit";
  }
  if (/variation\s*order/.test(lower) && /approved|acceptance|accept/.test(lower)) {
    return "Variation Order Approved";
  }

  const pct = raw.match(/(\d{1,3})\s*%/);
  if (/confirm|confirmation|deposit/.test(lower) && !/progress|final/.test(lower)) {
    return pct ? `${pct[1]}% Confirmation Deposit` : "Confirmation Deposit";
  }
  if (/progress/.test(lower)) {
    return pct ? `${pct[1]}% Progress Payment` : "Progress Payment";
  }
  if (/final/.test(lower)) {
    return pct ? `${pct[1]}% Final Payment` : "Final Payment";
  }
  if (/deposit/.test(lower)) {
    return pct ? `${pct[1]}% Deposit` : "Deposit";
  }

  const cleaned = raw
    .replace(/\s*·\s*due\s+\d{4}-\d{2}-\d{2}.*/i, "")
    .replace(/\s*·\s*status:.*/i, "")
    .replace(/\s*·\s*remaining\s+[\d.]+.*/i, "")
    .trim();
  if (cleaned && cleaned.toLowerCase() !== "invoice") return cleaned;
  return "Invoice";
}

export function resolveStatementActivityBadge(
  kind: ClarityOneStatementTxnKind,
  invoiceStatus?: string | null,
): ClarityOneStatementActivityBadge {
  const status = String(invoiceStatus ?? "").toLowerCase();
  if (status === "void" || status === "voided") return "VOIDED";
  if (status === "cancelled" || status === "canceled") return "CANCELLED";
  switch (kind) {
    case "acceptance":
      return "APPROVED";
    case "payment":
      return "PAYMENT";
    case "credit_note":
    case "refund":
      return "CREDIT";
    case "invoice":
    default:
      return "INVOICE";
  }
}

export function statementActivityBadgeClass(badge: ClarityOneStatementActivityBadge): string {
  switch (badge) {
    case "APPROVED":
    case "PAYMENT":
      return "bg-[#E8F5E9] text-[#2E7D32]";
    case "INVOICE":
      return "bg-[#1A2744]/10 text-[#1A2744]";
    case "CREDIT":
      return "bg-[#F3E8FF] text-[#6B21A8]";
    case "CANCELLED":
    case "VOIDED":
      return "bg-[#F0EDE6] text-[#6B7280]";
    default:
      return "bg-[#F0EDE6] text-[#6B7280]";
  }
}
